/* eslint-disable  @typescript-eslint/no-explicit-any */
import {
  Component,
  Host,
  h,
  State,
  Prop,
  Watch,
  Event,
  type EventEmitter,
  Listen,
  Element,
} from '@stencil/core';
import {FilterNode} from '../../../../result-page/facets-build-wrapper/filter-node';
import type {FacetResponse} from '../../../../../models/cose-api/response/facet-options.model';
import {cloneObjectWithNoReference} from '../../../../../utils/helpers/clone-object-with-no-reference';
import {FILTERS_ID_SEPARATOR} from '../../../../../constants/filterConstants';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';

@Component({
  tag: 'filters-single-facet-node',
  styleUrl: 'filters-single-facet-node.scss',
  shadow: false,
  scoped: true
})
export class FiltersSingleFacetNode {
  @Element() el!: HTMLFiltersSingleFacetNodeElement;

  @Prop() readonly labelProvider?: labelProviderInterface;
  @Prop() readonly filterNode: FilterNode | FacetResponse;
  @Prop() readonly preselectedFilters: string[];
  @Prop() readonly forceCollapse: boolean = false;

  @Watch('filterNode')
  onFilterNodeChange(newValue: FilterNode) {
      if(this.preselectedFilters && !newValue.children.find(f => f.selected)) {
        this.localFilterNode = this.selectChildrenByPreselected(cloneObjectWithNoReference(newValue), cloneObjectWithNoReference(this.preselectedFilters));
      } else {
        this.localFilterNode = cloneObjectWithNoReference(newValue);
      }
      this.createLocalState(this.filterNode as unknown as FilterNode);
  }

  @Event({
    eventName: 'changed',
    bubbles: false,
  })
    changedEmitter: EventEmitter<FilterNode>;

  @Listen('search:productCatalogWidgetClicked', { target: 'window' })
  handleProductCatalogWidgetClicked() {
      const widgetAlreadyClicked = window.sessionStorage.getItem('productCatalogWidgetClicked');
      if(widgetAlreadyClicked === null){
        this.productCatalogFacetHiglight = true;
        setTimeout(() => {
            this.productCatalogFacetHiglight = false;
        }, 2000);
      }
  }

  @State() localFiltersState: FilterNode[] = [];
  @State() productCatalogFacetHiglight = false;
  @State() localFilterNode: FilterNode | FacetResponse;
  @State() collapsed: boolean = false;


  componentWillLoad() {
      if(this.preselectedFilters && !this.filterNode.children.find(f=> f.selected)) {
        this.localFilterNode = this.selectChildrenByPreselected(cloneObjectWithNoReference(this.filterNode), cloneObjectWithNoReference(this.preselectedFilters));
      } else {
        this.localFilterNode = cloneObjectWithNoReference(this.filterNode);
      }
      this.createLocalState(this.filterNode);

      if(this.forceCollapse) {
        this.collapsed = true;
      }
  }


  private createLocalState(treeNode: FilterNode | FacetResponse): void {
      const localFiltersState = [];
      treeNode.children.forEach((node) => {
          const nodeIndex = localFiltersState.findIndex(filter => filter.uniqueId === node.uniqueId);

          if (node.selected) {
            if (nodeIndex === -1) {
              localFiltersState.push(node);
            }
          } else {
            if (nodeIndex !== -1) {
              localFiltersState.splice(nodeIndex, 1);
            }
          }
      });
      this.localFiltersState = localFiltersState;
  }

  render() {
      return (
        <Host>
          <div class="facet facet-product-catalog" style={this.productCatalogFacetHiglight ? {zIndex: '999'} : null}>
            {this.renderHeader()}
            <div
              class={`facet__body-single-option facet__body-single-option${this.handleOpenView}`}
            >
              <div
                class={'facet__accordion facet__accordion'}
              >
                <se-list option="filter" selectedColor="primary" class="single-option-filter-list">
                  {this.renderList(this.localFilterNode.children as FilterNode[])}
                </se-list>
              </div>
            </div>
          </div>
        </Host>
      );
  }

  private selectChildrenByPreselected(facet: FilterNode | FacetResponse, array: string[]): FilterNode | FacetResponse {
      const localFilterNode = { ...facet };
      let firstPreselectedValue: string | undefined;

      const preselectedFiltersArrays = array.map(uniqueId => uniqueId.split(FILTERS_ID_SEPARATOR));

      const matchingPreselectedArray = preselectedFiltersArrays.find(uniqueArray => uniqueArray[0] === (localFilterNode as FacetResponse).id);

      if (matchingPreselectedArray) {
        firstPreselectedValue = matchingPreselectedArray[1];
      }

      if (firstPreselectedValue && 'children' in localFilterNode) {
        localFilterNode.children.forEach(f => {
            if (f.value === firstPreselectedValue) {
              f.selected = true;
            }
        });
      }

      return localFilterNode;
  }

  private renderList(filteredChildren: FilterNode[]) {
      return (
        <div>
          {filteredChildren.map((f,i) =>
            <se-list-item
              key={i + f.uniqueId}
              item={`${f.displayName} (${f.count})`}
              selected={f.selected}
              className="single-option-item-label"
              onClick={() => {
                  const newState = cloneObjectWithNoReference(this.localFilterNode);
                  newState.children.map(filter => {
                      if(filter.selected && f.value !== filter.value) {
                        filter.selected = false;
                      }
                  });
                  newState.children[i].selected = true;
                  this.localFilterNode = newState;

                  this.changedEmitter.emit(newState as FilterNode);
              }}
            >
            </se-list-item>
          )}
        </div>
      );
  }

  private onClickCollapse() {
      this.collapsed = !this.collapsed;
  }

  private renderLocalState() {
      return (this.localFiltersState.map((filter: FilterNode, index: number) => (
        `${filter.displayName}${index !== this.localFiltersState.length - 1 ? ', ' : ''}`
      )));
  }

  private renderHeader() {
      return (
        <div
          class={'ftn-header-wrapper'}
          onClick={() => this.onClickCollapse()}
        >
          <div class={'ftn-info-wrapper'}>
            <div class={'ftn-title'}>
              {this.localFilterNode.id === 'catalog'
                ? this.labelProvider('product.catalog.catalog', 'Catalog')
                : this.localFilterNode.displayName
              }
            </div>
            {this.localFiltersState.length > 0 ? <span class={'ftn-selected-list'}>
              {this.renderLocalState()}
            </span> : ''}
          </div>
          <div class={'buttons-wrapper'}>
            <se-icon size="small"> {this.collapsed ? 'arrow2_up' : 'arrow2_down'} </se-icon>
          </div>
        </div>
      );
  }

  private get handleOpenView() {
      if (this.collapsed) {
        return '--open';
      }
      return '--close';
  }
}
